import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Check,
  ChevronRight,
  ContentCopy,
  Key,
  MenuBook,
} from '@mui/icons-material';

const baseUrl = `${window.location.origin}/api`;

const loginExample = `curl --request POST '${baseUrl}/auth/login' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "username": "YOUR_INTEGRATION_USERNAME",
    "password": "YOUR_INTEGRATION_PASSWORD"
  }'`;

const casePayload = `{
  "PolicyStartDate": "12/08/2026",
  "PolicyExpiryDate": "11/08/2027",
  "QuoteNumber": "Q/2026/00125",
  "VehiclePlateCharacter": "B",
  "VehiclePlateNumber": "12345",
  "EngineNumber": "ENG123456",
  "ChassisNumber": "CHS123456789",
  "Make": "Toyota",
  "Model": "Corolla",
  "BodyType": "Saloon",
  "ManufacturingYear": "2024",
  "ReasonForInspection": "New Policy",
  "PolicyCoverType": "Comprehensive",
  "CoverDetails": "Own damage and third-party cover",
  "PreviousPolicyExpiryDate": "11/08/2026",
  "PreviousPolicyCoverType": "Third Party",
  "Remarks": "Created from partner system"
}`;

const createCaseExample = `curl --request POST '${baseUrl}/CaseCreationServices/CreateCase' \\
  --header 'Authorization: Bearer YOUR_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '${casePayload}'`;

const successResponse = `{
  "StatusCode": 0,
  "Message": "Case Created Successfully...",
  "WebReferenceNo": "TK178651245536891",
  "WebBrowserLink": "${window.location.origin}/inspection/TK178651245536891",
  "validation": {
    "StatusCode": 0,
    "Message": "Success",
    "ErrorList": []
  }
}`;

const errorResponse = `{
  "StatusCode": 1,
  "Message": "case creation failed",
  "WebReferenceNo": null,
  "WebBrowserLink": null,
  "validation": {
    "StatusCode": 1,
    "Message": "Failure",
    "ErrorList": ["Please Enter Quote Number, Mandatory"]
  }
}`;

const fields = [
  ['PolicyStartDate', 'string', true, 'DD/MM/YYYY'],
  ['PolicyExpiryDate', 'string', true, 'DD/MM/YYYY'],
  ['QuoteNumber', 'string', true, 'Insurer quote or proposal number'],
  ['VehiclePlateCharacter', 'string', true, 'Registration prefix or character'],
  ['VehiclePlateNumber', 'string', true, 'Vehicle registration number'],
  ['EngineNumber', 'string', true, 'Vehicle engine number'],
  ['ChassisNumber', 'string', true, 'Vehicle chassis or VIN number'],
  ['Make', 'string', true, 'Value returned by the Masters endpoint'],
  ['Model', 'string', true, 'Value returned by the Masters endpoint'],
  ['BodyType', 'string', true, 'Value returned by the Masters endpoint'],
  ['ManufacturingYear', 'string', true, 'Four-digit year (YYYY)'],
  ['ReasonForInspection', 'string', true, 'Value returned by the Masters endpoint'],
  ['PolicyCoverType', 'string', true, 'Requested policy cover'],
  ['CoverDetails', 'string', true, 'Cover description'],
  ['PreviousPolicyExpiryDate', 'string', true, 'DD/MM/YYYY'],
  ['PreviousPolicyCoverType', 'string', true, 'Previous cover description'],
  ['Remarks', 'string', false, 'Additional case notes'],
];

const sections = [
  ['overview', 'Overview'],
  ['authentication', 'Authentication'],
  ['masters', 'Master data'],
  ['create-case', 'Create a case'],
  ['request-schema', 'Request schema'],
  ['responses', 'Responses and errors'],
];

const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const CodeBlock = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#111827' }}>
      <Box sx={{ height: 38, px: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid #293241' }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
        <Button
          size="small"
          color="inherit"
          startIcon={copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
          onClick={copy}
          sx={{ ml: 'auto', color: '#cbd5e1', minWidth: 0, px: 1 }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
      <Box
        component="pre"
        aria-label={label}
        sx={{
          m: 0,
          p: 2,
          color: '#e5e7eb',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12.5,
          lineHeight: 1.65,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {code}
      </Box>
    </Box>
  );
};

const Section = ({ id, title, description, children }) => (
  <Box component="section" id={id} sx={{ scrollMarginTop: 88, pt: 0.5 }}>
    <Typography component="h2" variant="h4" sx={{ fontSize: '1.35rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
      {title}
    </Typography>
    {description && (
      <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
        {description}
      </Typography>
    )}
    <Box sx={{ mt: 2.25 }}>{children}</Box>
  </Box>
);

const Endpoint = ({ method, path, summary, children }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1.5, boxShadow: 'none' }}>
    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: '#f8fafc' }}>
      <Chip
        label={method}
        size="small"
        sx={{
          minWidth: 52,
          bgcolor: method === 'GET' ? '#e8f5e9' : '#e8f1fc',
          color: method === 'GET' ? '#237a3b' : '#135ea8',
          fontWeight: 800,
        }}
      />
      <Typography component="code" variant="body2" fontWeight={700} sx={{ wordBreak: 'break-all' }}>{path}</Typography>
    </Box>
    <Divider />
    <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
      <Typography variant="subtitle1" fontWeight={700}>{summary}</Typography>
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  </Paper>
);

const TopicNavigation = ({ compact = false }) => (
  <Stack spacing={0.25}>
    {sections.map(([id, label], index) => (
      <Button
        key={id}
        onClick={() => scrollTo(id)}
        endIcon={compact ? null : <ChevronRight sx={{ opacity: 0.5 }} />}
        sx={{
          justifyContent: 'space-between',
          color: index === 0 ? 'primary.main' : 'text.secondary',
          fontWeight: index === 0 ? 700 : 500,
          fontSize: compact ? 13 : 14,
          px: compact ? 0 : 1,
          py: compact ? 0.5 : 0.75,
          minWidth: 0,
          textTransform: 'none',
          '&:hover': { bgcolor: compact ? 'transparent' : 'action.hover', color: 'primary.main' },
        }}
      >
        {label}
      </Button>
    ))}
  </Stack>
);

const ApiDocumentationPage = () => (
  <Box id="main-content" sx={{ width: '100%', maxWidth: 1440, mx: 'auto', pb: 6 }}>
    <Box component="header" sx={{ pb: 3, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <MenuBook color="primary" fontSize="small" />
        <Typography variant="caption" color="primary.main" fontWeight={700}>DEVELOPER DOCUMENTATION</Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5}>
        <Typography component="h1" variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.25rem' }, letterSpacing: '-0.025em' }}>
          Motor Inspection API
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label="Case Creation" size="small" color="primary" variant="outlined" />
          <Chip label="v1" size="small" />
          <Chip label="Stable" size="small" sx={{ bgcolor: '#eaf6ed', color: '#237a3b' }} />
        </Stack>
      </Stack>
      <Typography color="text.secondary" sx={{ mt: 1.25, maxWidth: 760, lineHeight: 1.7 }}>
        Create motor pre-inspection cases from an insurer or partner system and return a secure customer inspection link.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }} sx={{ mt: 2.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Base URL</Typography>
          <Typography component="code" variant="body2" fontWeight={650} sx={{ display: 'block', mt: 0.25, wordBreak: 'break-all' }}>{baseUrl}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Protocol</Typography>
          <Typography variant="body2" fontWeight={650} sx={{ mt: 0.25 }}>HTTPS · JSON</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Authentication</Typography>
          <Typography variant="body2" fontWeight={650} sx={{ mt: 0.25 }}>Bearer token</Typography>
        </Box>
      </Stack>
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '210px minmax(0, 820px) 180px' }, gap: { xs: 2, lg: 4 }, alignItems: 'start' }}>
      <Box component="nav" aria-label="Documentation topics" sx={{ display: { xs: 'none', lg: 'block' }, position: 'sticky', top: 88 }}>
        <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>Case Creation API</Typography>
        <TopicNavigation />
      </Box>

      <Stack component="article" spacing={5} sx={{ minWidth: 0 }}>
        <Section
          id="overview"
          title="Overview"
          description="The Case Creation API follows a three-step flow. Authenticate once, read the permitted master values, then submit the case. A successful response contains the reference number and customer inspection URL."
        >
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 1.5 }}>
            <Stack divider={<Divider flexItem />} spacing={0}>
              {[
                ['1', 'Authenticate', 'Exchange your integration credentials for a short-lived access token.'],
                ['2', 'Read master values', 'Use current make, model, body type, plate character, and inspection reason values.'],
                ['3', 'Create the case', 'Submit the policy and vehicle data, then share the returned inspection link.'],
              ].map(([number, title, text]) => (
                <Box key={number} sx={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 1.5, py: 1.5, '&:first-of-type': { pt: 0 }, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}>{number}</Box>
                  <Box>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{text}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Section>

        <Section
          id="authentication"
          title="Authentication"
          description="Use the assigned integration account to obtain an access token. Send the token with every subsequent request in the Authorization header. Access tokens expire after 15 minutes; server integrations can authenticate again when required."
        >
          <Alert severity="info" icon={<Key />} sx={{ mb: 2 }}>
            Keep integration credentials on your server. Never embed them in browser or mobile application code.
          </Alert>
          <Endpoint method="POST" path="/auth/login" summary="Generate an access token">
            <CodeBlock code={loginExample} label="cURL" />
          </Endpoint>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            For authenticated calls, include <Box component="code" sx={{ px: 0.75, py: 0.25, bgcolor: 'action.hover', borderRadius: 0.5 }}>Authorization: Bearer YOUR_TOKEN</Box>.
          </Typography>
        </Section>

        <Section
          id="masters"
          title="Master data"
          description="Read current reference values before creating a case. This prevents rejected requests caused by unsupported makes, models, body types, inspection reasons, or plate characters."
        >
          <Endpoint method="GET" path="/CaseCreationServices/Masters" summary="Get accepted master values">
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Authorization:</strong> Bearer token required</Typography>
              <Typography variant="body2"><strong>Request body:</strong> None</Typography>
              <Typography variant="body2" color="text.secondary">Cache these values only for the current integration session so newly added master values remain available.</Typography>
            </Stack>
          </Endpoint>
        </Section>

        <Section
          id="create-case"
          title="Create a case"
          description="Creates an Open inspection case. Dates use DD/MM/YYYY and field names are case-sensitive. The returned browser link is intended for the customer completing the inspection."
        >
          <Endpoint method="POST" path="/CaseCreationServices/CreateCase" summary="Create a motor inspection request">
            <Stack spacing={2.5}>
              <CodeBlock code={createCaseExample} label="cURL" />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Example request body</Typography>
                <CodeBlock code={casePayload} label="JSON" />
              </Box>
            </Stack>
          </Endpoint>
        </Section>

        <Section
          id="request-schema"
          title="Request schema"
          description="Required properties must contain a non-empty value. Values documented as master data must match an active value returned by the Masters endpoint."
        >
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
            <Table size="small" aria-label="Case creation request schema">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell>Property</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Required</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map(([name, type, required, description]) => (
                  <TableRow key={name}>
                    <TableCell component="th" scope="row"><Typography component="code" variant="body2" fontWeight={650}>{name}</Typography></TableCell>
                    <TableCell><Typography component="code" variant="body2" color="text.secondary">{type}</Typography></TableCell>
                    <TableCell>{required ? <Chip label="Required" size="small" variant="outlined" /> : <Typography variant="body2" color="text.secondary">Optional</Typography>}</TableCell>
                    <TableCell>{description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section
          id="responses"
          title="Responses and errors"
          description="The integration uses a business status inside the JSON response. Read StatusCode and validation.ErrorList even when the HTTP response itself is 200."
        >
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip label="StatusCode 0" size="small" sx={{ bgcolor: '#eaf6ed', color: '#237a3b', fontWeight: 700 }} />
                <Typography variant="subtitle2">Case created</Typography>
              </Stack>
              <CodeBlock code={successResponse} label="Successful response" />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip label="StatusCode 1" size="small" sx={{ bgcolor: '#fdecec', color: '#b42318', fontWeight: 700 }} />
                <Typography variant="subtitle2">Validation failed</Typography>
              </Stack>
              <CodeBlock code={errorResponse} label="Validation response" />
            </Box>
          </Stack>
        </Section>
      </Stack>

      <Box component="aside" sx={{ display: { xs: 'none', lg: 'block' }, position: 'sticky', top: 88, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
        <Typography variant="overline" color="text.secondary">On this page</Typography>
        <TopicNavigation compact />
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          Need credentials or integration support? Contact the system administrator.
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default ApiDocumentationPage;
